import * as React from 'react';
import { useState } from 'react';

import { branch, compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToFluxibleContext from 'js/lib/connectToFluxibleContext';
import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { ApiButton } from '@coursera/coursera-ui';
import {
  API_BEFORE_SEND,
  API_ERROR,
  API_IN_PROGRESS,
  API_SUCCESS,
} from '@coursera/coursera-ui/lib/constants/sharedConstants';
import type { ApiStatus } from '@coursera/coursera-ui/lib/constants/sharedConstants';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import { getUnsubscribeStatus, unsubscribe } from 'bundles/account-settings/actions/notificationApi';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import UnsubscribeStore from 'bundles/account-settings/stores/UnsubscribeStore';
import type { UnsubscribeStatus } from 'bundles/account-settings/types/UnsubscribeStatus';
import waitForStores from 'bundles/phoenix/lib/waitForStores';

import _t from 'i18n!nls/account-settings';

import 'css!bundles/account-settings/styl/index';

import { UnsubscribeCopyProvider } from './UnsubscribeCopyProvider';

type InitialProps = {
  invalidTokenError?: string;
  unsubscribeStatus: UnsubscribeStatus;
};

type Props = InitialProps & {
  router: InjectedRouter;
};

export const UnsubscribePage: React.FC<Props> = ({
  invalidTokenError,
  unsubscribeStatus,
  router,
  router: {
    params: { token },
  },
}) => {
  const [unsubscribeButtonStatus, setUnsubscribeButtonStatus] = useState<ApiStatus>(API_BEFORE_SEND);

  const onUnsubscribe = () => {
    setUnsubscribeButtonStatus(API_IN_PROGRESS);
    unsubscribe(token)
      .then(() => {
        setUnsubscribeButtonStatus(API_SUCCESS);
      })
      .catch(() => {
        setUnsubscribeButtonStatus(API_ERROR);
        // reset button state
        setTimeout(() => {
          setUnsubscribeButtonStatus(API_BEFORE_SEND);
        }, 3000);
      });
  };

  const onFewerEmails = () => {
    router.replace('/account-settings');
    SettingsActionCreators.setTopSection(SettingsConstants.TopSections.EMAIL);
  };

  return (
    <UnsubscribeCopyProvider status={unsubscribeStatus}>
      {({ header, subheader, cta }) => (
        <div className="rc-UnsubscribePage styleguide">
          <div className="bt3-container body-container unsubscribe-message-container">
            <FormattedMessage
              data-e2e="formatted-message-test"
              message="{description}"
              description={<h1 className="unsubscribe-title">{header}</h1>}
            />
            {invalidTokenError && <span className="error-message">{invalidTokenError}</span>}
            {unsubscribeStatus && (
              <div className="unsubscribe-button-wrapper">
                <FormattedMessage
                  message="{description}"
                  description={<h2 className="unsubscribe-question">{subheader}</h2>}
                />
                <ApiButton
                  type="primary"
                  size="md"
                  rootClassName="m-l-1s"
                  onClick={onUnsubscribe}
                  apiStatus={unsubscribeButtonStatus}
                  apiStatusAttributesConfig={{
                    label: {
                      API_BEFORE_SEND: <FormattedMessage message={cta.primary} />,
                      API_IN_PROGRESS: <FormattedMessage message={_t('Unsubscribing...')} />,
                      API_SUCCESS: <FormattedMessage message={_t('Successfully unsubscribed.')} />,
                      API_ERROR: <FormattedMessage message={_t('Error, please try again.')} />,
                    },
                    disabled: {
                      API_BEFORE_SEND: false,
                      API_IN_PROGRESS: true,
                      API_SUCCESS: true,
                      API_ERROR: true,
                    },
                  }}
                />
                <div className="un-divider" />
                {cta.secondary && (
                  <ApiButton
                    type={cta.secondaryButtonType ?? 'primary'}
                    size="md"
                    label={cta.secondary}
                    rootClassName="m-l-1s"
                    onClick={onFewerEmails}
                    apiStatus={unsubscribeButtonStatus}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </UnsubscribeCopyProvider>
  );
};

export default compose<Props, InitialProps>(
  connectToRouter((router) => ({ router })),
  // TODO: connectToStores<Props, InputProps, Stores>
  connectToStores(['UnsubscribeStore'], ({ UnsubscribeStore: { unsubscribeStatus, invalidTokenError } }, props) => ({
    ...props,
    unsubscribeStatus,
    invalidTokenError,
  })),
  branch(
    ({ unsubscribeStatus, invalidTokenError }: InitialProps) => !(invalidTokenError || unsubscribeStatus),
    connectToFluxibleContext(({ executeAction }, { router }) => {
      const { token } = router.params;
      executeAction(getUnsubscribeStatus.bind(null, token));
    })
  ),
  // @ts-expect-error FIXME first paramter should be array of store names
  waitForStores([UnsubscribeStore], (context, props) => {
    return {
      ...props,
      invalidTokenError: context.UnsubscribeStore.getInvalidTokenError(),
      unsubscribeStatus: context.UnsubscribeStore.getUnsubscribeStatus(),
    };
  })
)(UnsubscribePage);
